import React, { useState } from 'react';
import AIInsightsChatbot from '../components/Dashboard/AIInsightsChatbot';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronDown, ChevronUp, Upload, FileText, Image, AlertCircle, CheckCircle, Clock, Search, Download, Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { poReconciliationData, matchSuggestions, poRegions, poVendors } from '../data/poData';

const POReconciliation = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filters, setFilters] = useState({
    dateRange: 'all',
    brand: 'all',
    channel: 'all',
    vendor: 'All Vendors',
    status: 'all',
    region: 'All Regions'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPO, setSelectedPO] = useState(null);

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  const getPresetRange = (preset) => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let start = new Date(end);

    if (preset === 'last_7') {
      start.setDate(end.getDate() - 6);
    } else if (preset === 'last_30') {
      start.setDate(end.getDate() - 29);
    } else if (preset === 'this_month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (preset === 'last_month') {
      const firstOfThisMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 24 * 60 * 60 * 1000);
      start = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      return { from: start, to: lastMonthEnd };
    }
    return { from: start, to: end };
  };

  const [preset, setPreset] = useState('last_30');
  const [dateRange, setDateRange] = useState(() => getPresetRange('last_30'));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handlePresetChange = (value) => {
    setPreset(value);
    const range = getPresetRange(value);
    setDateRange(range);
  };

  const rangeLabel = dateRange?.from && dateRange?.to
    ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    : 'Select date range';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Pending Review':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Dispute':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'Awaiting Invoice':
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Completed': 'bg-green-100 text-green-800 hover:bg-green-200',
      'Pending Review': 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
      'Dispute': 'bg-red-100 text-red-800 hover:bg-red-200',
      'Awaiting Invoice': 'bg-blue-100 text-blue-800 hover:bg-blue-200'
    };
    return (
      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const getVarianceBadge = (variance, matched) => {
    if (!matched) {
      return <Badge className="bg-red-100 text-red-800">Not Matched</Badge>;
    }
    if (Math.abs(variance) > 5) {
      return <Badge className="bg-yellow-100 text-yellow-800">{variance > 0 ? '+' : ''}{variance.toFixed(1)}%</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800">{variance > 0 ? '+' : ''}{variance.toFixed(1)}%</Badge>;
  };

  const toggleRowExpansion = (poId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(poId)) {
      newExpanded.delete(poId);
    } else {
      newExpanded.add(poId);
    }
    setExpandedRows(newExpanded);
  };

  const filteredData = poReconciliationData.filter(po => {
    const matchesSearch = po.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilters = 
      (filters.brand === 'all' || po.brand === filters.brand) &&
      (filters.channel === 'all' || po.channel === filters.channel) &&
      (filters.vendor === 'All Vendors' || po.vendor === filters.vendor) &&
      (filters.status === 'all' || po.status === filters.status) &&
      (filters.region === 'All Regions' || po.region === filters.region);
    
    return matchesSearch && matchesFilters;
  });

  const suggestions = matchSuggestions.find(s => s.poId === selectedPO?.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            PO Reconciliation
          </h1>
          <p className="text-gray-600">Purchase Order Matching & Invoice Verification</p>
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 px-4">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {rangeLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3">
                  <Calendar
                    mode="range"
                    numberOfMonths={2}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range || { from: null, to: null });
                    }}
                    initialFocus
                  />
                  <div className="flex justify-end gap-2 pt-3">
                    <Button variant="ghost" size="sm" onClick={() => setIsCalendarOpen(false)}>Cancel</Button>
                    <Button size="sm" onClick={() => setIsCalendarOpen(false)}>Apply</Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="min-w-[220px]">
              <Select value={preset} onValueChange={handlePresetChange}>
                <SelectTrigger aria-label="Date range preset">
                  <SelectValue placeholder="Select preset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7">Last 7 days</SelectItem>
                  <SelectItem value="last_30">Last 30 days</SelectItem>
                  <SelectItem value="this_month">This month</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-4 space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Filters & Search</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-8 gap-4">
                  <div className="md:col-span-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search POs, vendors, brands..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <Select value={filters.region} onValueChange={(value) => setFilters({...filters, region: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Region" />
                    </SelectTrigger>
                    <SelectContent>
                      {poRegions.map(region => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filters.channel} onValueChange={(value) => setFilters({...filters, channel: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Channel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Channels</SelectItem>
                      <SelectItem value="TV">TV</SelectItem>
                      <SelectItem value="Digital">Digital</SelectItem>
                      <SelectItem value="Print">Print</SelectItem>
                      <SelectItem value="Radio">Radio</SelectItem>
                      <SelectItem value="Outdoor">Outdoor</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filters.vendor} onValueChange={(value) => setFilters({...filters, vendor: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {poVendors.map(vendor => (
                        <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Pending Review">Pending Review</SelectItem>
                      <SelectItem value="Dispute">Dispute</SelectItem>
                      <SelectItem value="Awaiting Invoice">Awaiting Invoice</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex items-center gap-3 justify-end lg:col-span-2">
                    <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 min-w-[140px] shrink-0">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload PO
                    </Button>
                    <Button variant="outline" size="icon" title="Export" className="shrink-0">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PO Table */}
            <Card>
              <CardHeader>
                <CardTitle>Purchase Orders ({filteredData.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Table Headers */}
                <div className="grid [grid-template-columns:repeat(9,minmax(0,1fr))_auto] gap-4 items-center p-4 bg-gray-50 border rounded-lg mb-4 text-sm font-semibold text-gray-700">
                  <div>PO ID</div>
                  <div>Date</div>
                  <div>Channel</div>
                  <div>Vendor</div>
                  <div>Region</div>
                  <div>PO Amount</div>
                  <div>Invoice Amount</div>
                  <div>Actual Spend</div>
                  <div>Matched?</div>
                  <div>Actions</div>
                </div>
                
                <div className="space-y-4">
                  {filteredData.map((po) => (
                    <div key={po.id} className="border rounded-lg">
                      {/* Main Row */}
                      <div className="bg-white p-4 hover:bg-gray-50 transition-colors">
                        <div className="grid [grid-template-columns:repeat(9,minmax(0,1fr))_auto] gap-4 items-center">
                          <div className="font-medium text-blue-600">{po.id}</div>
                          <div className="text-sm text-gray-600">{new Date(po.date).toLocaleDateString()}</div>
                          <div className="text-sm text-gray-600">{po.channel}</div>
                          <div className="text-sm">{po.vendor}</div>
                          <div className="text-sm">
                            <Badge className="bg-blue-100 text-blue-800 text-xs">{po.region}</Badge>
                          </div>
                          <div className="text-sm font-medium">{formatCurrency(po.poAmount)}</div>
                          <div className="text-sm">{formatCurrency(po.invoiceAmount)}</div>
                          <div className="text-sm">{formatCurrency(po.actualSpend)}</div>
                          <div>{getVarianceBadge(po.variance, po.matched)}</div>
                          <div className="flex space-x-2 justify-end flex-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleRowExpansion(po.id)}
                            >
                              {expandedRows.has(po.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedPO(po)}
                            >
                              Match
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedRows.has(po.id) && (
                        <div className="bg-blue-50 p-4 border-t">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Invoices */}
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center">
                                <FileText className="h-4 w-4 mr-2" />
                                Invoices ({po.invoices.length})
                              </h4>
                              <div className="space-y-2">
                                {po.invoices.map((invoice) => (
                                  <div key={invoice.id} className="bg-white p-3 rounded border text-sm">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-medium">{invoice.id}</div>
                                        <div className="text-gray-600">{formatCurrency(invoice.amount)}</div>
                                        <div className="text-xs text-gray-500">{invoice.date}</div>
                                      </div>
                                      <Badge className={`text-xs ${
                                        invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                        invoice.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                      }`}>
                                        {invoice.status}
                                      </Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Attachments */}
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center">
                                <FileText className="h-4 w-4 mr-2" />
                                Attachments ({po.attachments.length})
                              </h4>
                              <div className="space-y-2">
                                {po.attachments.map((file, index) => (
                                  <div key={index} className="bg-white p-3 rounded border text-sm">
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-4 w-4 text-blue-500" />
                                      <span className="text-blue-600 hover:underline cursor-pointer">{file}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Screenshots */}
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center">
                                <Image className="h-4 w-4 mr-2" />
                                Screenshots ({po.screenshots.length})
                              </h4>
                              <div className="space-y-2">
                                {po.screenshots.map((file, index) => (
                                  <div key={index} className="bg-white p-3 rounded border text-sm">
                                    <div className="flex items-center space-x-2">
                                      <Image className="h-4 w-4 text-green-500" />
                                      <span className="text-blue-600 hover:underline cursor-pointer">{file}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Match Suggestions</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPO ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded border">
                      <h4 className="font-medium text-blue-900 mb-2">Selected PO</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">ID:</span> {selectedPO.id}</div>
                        <div><span className="font-medium">Vendor:</span> {selectedPO.vendor}</div>
                        <div><span className="font-medium">Amount:</span> {formatCurrency(selectedPO.poAmount)}</div>
                      </div>
                    </div>

                    {suggestions ? (
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-900">Auto-Match Suggestions</h4>
                        {suggestions.suggestions.map((suggestion, index) => (
                          <div key={index} className="p-3 bg-green-50 rounded border">
                            <div className="flex justify-between items-start mb-2">
                              <div className="text-sm font-medium text-green-900">{suggestion.vendor}</div>
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                {suggestion.confidence}% Match
                              </Badge>
                            </div>
                            <div className="text-sm text-green-700 space-y-1">
                              <div>Amount: {formatCurrency(suggestion.amount)}</div>
                              <div>Date: {suggestion.date}</div>
                              <div className="text-xs">{suggestion.reason}</div>
                            </div>
                            <Button size="sm" className="w-full mt-2 bg-green-600 hover:bg-green-700">
                              Apply Match
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No auto-match suggestions available
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-8">
                    Select a PO to view match suggestions
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{filteredData.length}</div>
                    <div className="text-sm text-gray-600">Total POs</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center text-sm">
                    <div>
                      <div className="font-semibold text-green-600">
                        {filteredData.filter(po => po.matched).length}
                      </div>
                      <div className="text-gray-600">Matched</div>
                    </div>
                    <div>
                      <div className="font-semibold text-red-600">
                        {filteredData.filter(po => !po.matched).length}
                      </div>
                      <div className="text-gray-600">Pending</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <AIInsightsChatbot />
    </div>
  );
};

export default POReconciliation;